import { ArrowUpRight, ClockWatch, RecordVideoCamera } from '@griply/assets';
import {
  CalendarEventDetails,
  CalendarEventFormFields,
  CalendarEventResponseStatus,
} from '@griply/types';
import { getCalendarEventGuestList } from '@griply/utils';
import React, { useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { Button, Colors, Sizes, Variants } from 'shared/components/ui/button';
import {
  CustomFormSelect,
  Size as SelectSize,
} from 'shared/components/ui/custom-form-select';
import { Divider } from 'shared/components/ui/divider';
import { useToday } from 'shared/contexts/today';
import { useOpenMenu } from 'shared/hooks/use-open-menu';
import {
  DateFormatOptions,
  TimeFormatOptions,
} from 'shared/types/date-format-options';
import { WeekDays } from 'shared/types/week-days';

import { EventFormHeader } from './event-form-header';
import * as Styled from './event-invitee-view.style';
import { EventProposeTimeMenu } from './event-propose-time-menu';
import { EventRow } from './event-row';
import { EventDateTimeRow } from './rows/event-date-time-row';
import { EventDescriptionRow } from './rows/event-description-row';
import { EventGuestsRow } from './rows/event-guests-row';
import { EventLocationRow } from './rows/event-location-row';
import { EventVideoCallRow } from './rows/event-video-call-row';

export type EventInviteeViewProps = {
  fields: CalendarEventFormFields;
  dateFormat: DateFormatOptions;
  timeFormat: TimeFormatOptions;
  weekStartsOn: WeekDays;
  isLoadingDetails?: boolean;
  videoCallUrl?: string | null;
  // the conferencing system's own label ("Zoom Meeting"); read-only here — an invitee cannot
  // change the event, so the row names the call without offering to replace it
  videoCallProviderName?: string | null;
  descriptionHtml?: string | null;
  // Outlook omits the organizer from `guests`, so the display list merges them back in
  organizer?: CalendarEventDetails['organizer'];
  responseStatus?: CalendarEventResponseStatus | null;
  isResponding?: boolean;
  hasRespondError?: boolean;
  hasProposeError?: boolean;
  onRespond: (status: CalendarEventResponseStatus) => void;
  onProposeNewTime: (start: Date, end: Date, comment: string) => void;
};

export const EventInviteeView: React.FC<EventInviteeViewProps> = ({
  fields,
  dateFormat,
  timeFormat,
  weekStartsOn,
  isLoadingDetails,
  videoCallUrl,
  videoCallProviderName,
  descriptionHtml,
  organizer,
  responseStatus,
  isResponding,
  hasRespondError,
  hasProposeError,
  onRespond,
  onProposeNewTime,
}) => {
  const { t } = useTranslation();
  const today = useToday();
  const proposeTriggerRef = useRef<HTMLDivElement>(null);
  const {
    menuOpen: proposeOpen,
    openMenu: openPropose,
    closeMenu: closePropose,
  } = useOpenMenu();

  // This view only renders for an invitee, so the organizer is never the viewer and always
  // belongs in the list — Outlook leaves them out of `guests` entirely.
  const guestList = useMemo(
    () => getCalendarEventGuestList({ guests: fields.guests, organizer }),
    [fields.guests, organizer],
  );

  const responseOptions = useMemo(
    () => [
      {
        label: t('forms.event.rsvp.options.yes'),
        value: CalendarEventResponseStatus.Accepted,
      },
      {
        label: t('forms.event.rsvp.options.no'),
        value: CalendarEventResponseStatus.Declined,
      },
      {
        label: t('forms.event.rsvp.options.maybe'),
        value: CalendarEventResponseStatus.Tentative,
      },
    ],
    [t],
  );

  const onChangeResponse = (value?: string) =>
    value && onRespond(value as CalendarEventResponseStatus);

  const onSubmitProposal = (start: Date, end: Date, comment: string) => {
    onProposeNewTime(start, end, comment);
    closePropose();
  };

  const hasDetailRows =
    !!videoCallUrl || !!fields.location || !!isLoadingDetails;

  return (
    <Styled.Container>
      <EventFormHeader value={fields.title} disabled />
      <Divider noMargin />
      <Styled.ScrollArea>
        <Styled.Body>
          <Styled.Section>
            {fields.allDay && (
              <EventRow icon={ClockWatch}>
                <Styled.ReadOnlyValue>
                  {t('general.labels.all-day')}
                </Styled.ReadOnlyValue>
              </EventRow>
            )}

            <EventDateTimeRow
              label={t('forms.event.fields.starts.label')}
              date={fields.startDate}
              time={fields.startTime}
              allDay={fields.allDay}
              today={today}
              weekStartsOn={weekStartsOn}
              dateFormat={dateFormat}
              timeFormat={timeFormat}
              timePlaceholder={t('forms.event.fields.starts.placeholder')}
              disabled
            />

            <EventDateTimeRow
              label={t('forms.event.fields.ends.label')}
              date={fields.endDate}
              time={fields.endTime}
              allDay={fields.allDay}
              today={today}
              weekStartsOn={weekStartsOn}
              dateFormat={dateFormat}
              timeFormat={timeFormat}
              timePlaceholder={t('forms.event.fields.ends.placeholder')}
              disabled
            />

            <EventRow>
              <Styled.ProposeAnchor ref={proposeTriggerRef}>
                <Button
                  variant={Variants.Outlined}
                  size={Sizes.Small}
                  color={Colors.Inherit}
                  endIcon={ArrowUpRight}
                  onClick={openPropose}
                  type="button"
                >
                  {t('forms.event.propose-time.trigger')}
                </Button>
                {proposeOpen && (
                  <EventProposeTimeMenu
                    referenceElement={proposeTriggerRef}
                    fields={fields}
                    dateFormat={dateFormat}
                    timeFormat={timeFormat}
                    weekStartsOn={weekStartsOn}
                    onSubmit={onSubmitProposal}
                    onClose={closePropose}
                  />
                )}
              </Styled.ProposeAnchor>
            </EventRow>
          </Styled.Section>
        </Styled.Body>

        {!!hasProposeError && (
          <Styled.ErrorText role="alert">
            {t('forms.event.propose-time.error')}
          </Styled.ErrorText>
        )}

        <Divider noMargin />

        <Styled.Body>
          <Styled.Section>
            <EventGuestsRow
              value={guestList}
              isLoading={isLoadingDetails}
              disabled
            />
          </Styled.Section>
        </Styled.Body>

        {hasDetailRows && (
          <>
            <Divider noMargin />
            <Styled.Body>
              <Styled.Section>
                {!!videoCallUrl && (
                  <EventVideoCallRow
                    url={videoCallUrl}
                    providerName={videoCallProviderName}
                    icon={RecordVideoCamera}
                  />
                )}
                {(!!fields.location || !!isLoadingDetails) && (
                  <EventLocationRow
                    value={fields.location}
                    isLoading={isLoadingDetails}
                    disabled
                  />
                )}
              </Styled.Section>
            </Styled.Body>
          </>
        )}

        {!!fields.description && (
          <>
            <Divider noMargin />
            <Styled.Body>
              <Styled.Section>
                <EventDescriptionRow
                  value={fields.description}
                  html={descriptionHtml}
                  disabled
                />
              </Styled.Section>
            </Styled.Body>
          </>
        )}
      </Styled.ScrollArea>

      <Divider noMargin />

      <Styled.RsvpRow>
        <span>{t('forms.event.rsvp.label')}</span>
        <Styled.RsvpControl>
          <CustomFormSelect
            value={responseStatus ?? undefined}
            options={responseOptions}
            onChange={onChangeResponse}
            placeholder={t('forms.event.rsvp.placeholder')}
            size={SelectSize.ExtraSmall}
            disabled={isResponding || isLoadingDetails}
          />
        </Styled.RsvpControl>
      </Styled.RsvpRow>

      {!!hasRespondError && (
        <Styled.ErrorText role="alert">
          {t('forms.event.rsvp.error')}
        </Styled.ErrorText>
      )}
    </Styled.Container>
  );
};
